import { consoleCanSetPortalAddress, type ConsoleRole } from "@flexfreight/core";
import {
  Badge,
  Banner,
  Button,
  Combobox,
  Dialog,
  ErrorState,
  PageHeader,
  PhoneField,
  Section,
  Select,
  SkeletonText,
  TextField,
  Toggle,
  US_STATES,
  notify,
  useConfirm,
} from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { SESSION_KEY } from "../../auth/ConsoleGate";

type Settings = {
  companyName: string | null;
  supportEmail: string | null;
  supportPhone: string | null;
  websiteUrl: string | null;
  billingName: string | null;
  billingAddress1: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
  billingPhone: string | null;
  /** Customer portal address (https origin). Null = the server's WEB_ORIGIN. */
  portalOrigin: string | null;
  signupPolicy: "open" | "approval" | "invite_only";
  notifyNewSignup: boolean;
  notifyTicketBreach: boolean;
  notifyShipmentException: boolean;
  updatedAt: string | null;
};
const POLICY: Record<Settings["signupPolicy"], { label: string; help: string }> = {
  open: { label: "Open", help: "Anyone can create an account. New accounts appear in Signups & approvals. This is how the portal works today." },
  approval: { label: "Review new accounts", help: "Anyone can sign up and each new account goes to Signups & approvals for review. Whether held accounts can quote before approval is not decided yet, so the portal does not block them." },
  invite_only: { label: "Invitation only", help: "The public signup form is closed. Customers join only through an invitation from FlexFreight or from a colleague." },
};
type Portal = { inUse: string | null; fallback: string | null };
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const blank = (v: string) => (v.trim() === "" ? null : v.trim());
const ALERTS = [
  { key: "notifyNewSignup", label: "New customer signup", description: "When someone opens an account from the portal." },
  { key: "notifyTicketBreach", label: "Ticket past its response target", description: "When a support ticket misses its first-response time." },
  { key: "notifyShipmentException", label: "Shipment exception", description: "When a carrier reports an exception on any shipment." },
] as const;

/** Admin Center > Company & policies (A.39). */
export function CompanyPage() {
  const me = useConsoleMe();
  const canEdit = me.capabilities.includes("settings:manage");
  // Every customer link points at the portal address, so only the console owner (strong second factor) may change it.
  const canEditPortal = consoleCanSetPortalAddress(me.role as ConsoleRole, me.mfaEnabled);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["console", "company"], queryFn: () => api<{ settings: Settings; portal?: Portal }>("/v1/console/company") });
  const [f, setF] = useState<Settings | null>(null);
  useEffect(() => {
    if (q.data && !f) setF(q.data.settings);
  }, [q.data, f]);
  const save = useMutation({
    mutationFn: (s: Settings) =>
      api<{ settings: Settings; changed: string[]; portal?: Portal }>("/v1/console/company", {
        method: "PUT",
        body: {
          companyName: blank(s.companyName ?? ""),
          supportEmail: blank(s.supportEmail ?? ""),
          supportPhone: blank(s.supportPhone ?? ""),
          websiteUrl: blank(s.websiteUrl ?? ""),
          billingName: blank(s.billingName ?? ""),
          billingAddress1: blank(s.billingAddress1 ?? ""),
          billingCity: blank(s.billingCity ?? ""),
          billingState: blank(s.billingState ?? ""),
          billingPostalCode: blank(s.billingPostalCode ?? ""),
          billingPhone: blank(s.billingPhone ?? ""),
          portalOrigin: blank(s.portalOrigin ?? ""),
          signupPolicy: s.signupPolicy, notifyNewSignup: s.notifyNewSignup, notifyTicketBreach: s.notifyTicketBreach, notifyShipmentException: s.notifyShipmentException },
      }),
    onSuccess: (r) => {
      setF(r.settings);
      void qc.invalidateQueries({ queryKey: ["console", "company"] });
      // The console's own links into the portal come from /v1/console/me: refresh them with the new address.
      if (r.changed.includes("portalOrigin")) void qc.invalidateQueries({ queryKey: SESSION_KEY });
      notify.success(r.changed.length ? "Saved" : "No changes", r.changed.length ? "The change is in the audit log." : undefined);
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  const header = (actions?: { dirty: boolean; onSave: () => void; onDiscard: () => void }) => (
    <PageHeader
      eyebrow="Admin Center"
      title="Company & policies"
      subtitle={`How FlexFreight appears to customers and who can open an account.${canEdit ? "" : " You can view these settings; an owner or admin can change them."}`}
      secondaryActions={
        actions?.dirty ? (
          <>
            <Badge tone="warning">Unsaved changes</Badge>
            <Button variant="ghost" onClick={actions.onDiscard}>Discard</Button>
          </>
        ) : null
      }
      primaryAction={canEdit && actions ? <Button variant="primary" disabled={!actions.dirty} loading={save.isPending} onClick={actions.onSave}>Save changes</Button> : undefined}
    />
  );
  if (q.isError) {
    return (
      <section className="ffa-page">
        {header()}
        <ErrorState title="Couldn't load company settings" message={message(q.error)} onRetry={() => void q.refetch()} />
      </section>
    );
  }
  if (!f) {
    return (
      <section className="ffa-page" aria-busy="true">
        {header()}
        <div className="ffa-company">
          {[0, 1, 2].map((i) => (
            <Section key={i}><SkeletonText lines={4} /></Section>
          ))}
        </div>
        <span className="ffx-sr">Loading…</span>
      </section>
    );
  }
  const set = (patch: Partial<Settings>) => setF({ ...f, ...patch });
  const dirty = JSON.stringify({ ...f, updatedAt: null }) !== JSON.stringify({ ...q.data!.settings, updatedAt: null });
  const portalLocked = !canEdit || !canEditPortal;
  return (
    <section className="ffa-page">
      {header({ dirty, onSave: () => save.mutate(f), onDiscard: () => setF(q.data!.settings) })}
      <form
        className="ffa-company"
        onSubmit={(e) => {
          e.preventDefault();
          if (canEdit && dirty) save.mutate(f);
        }}
      >
        <Section fieldset disabled={!canEdit} title="Company profile" description="The sender name and postal address for marketing email are in Marketing > Sender settings.">
          <div className="ffx-fields">
            <TextField className="ffx-span" label="Company name" value={f.companyName ?? ""} onChange={(e) => set({ companyName: e.target.value })} maxLength={160} autoComplete="organization" optional />
            <TextField label="Support email" type="email" value={f.supportEmail ?? ""} onChange={(e) => set({ supportEmail: e.target.value })} autoComplete="email" example="help@flexfreight.net" optional />
            <PhoneField label="Support phone" value={f.supportPhone ?? ""} onChange={(v) => set({ supportPhone: v })} maxLength={40} optional />
            <TextField className="ffx-span" label="Website" type="url" inputMode="url" value={f.websiteUrl ?? ""} onChange={(e) => set({ websiteUrl: e.target.value })} example="https://flexfreight.net" spellCheck={false} optional />
          </div>
        </Section>
        <Section fieldset disabled={!canEdit} title="Billing address" description="FlexFreight as the bill-to party on carrier bills of lading (Old Dominion eBOLs). Fill in every field; until then Old Dominion bills the shipper's address.">
          <div className="ffx-fields">
            <TextField className="ffx-span" label="Bill-to name" value={f.billingName ?? ""} onChange={(e) => set({ billingName: e.target.value })} maxLength={160} optional />
            <TextField className="ffx-span" label="Street address" value={f.billingAddress1 ?? ""} onChange={(e) => set({ billingAddress1: e.target.value })} maxLength={160} autoComplete="street-address" optional />
            <TextField label="City" value={f.billingCity ?? ""} onChange={(e) => set({ billingCity: e.target.value })} maxLength={80} autoComplete="address-level2" optional />
            <Combobox label="State" options={US_STATES.map((s) => ({ value: s.value, label: s.label, detail: s.value }))} value={f.billingState ?? null} valueLabel={f.billingState ?? undefined} onChange={(v) => set({ billingState: v })} clearable optional emptyText="No state matches." />
            <TextField label="ZIP" value={f.billingPostalCode ?? ""} onChange={(e) => set({ billingPostalCode: e.target.value })} maxLength={10} inputMode="numeric" autoComplete="postal-code" optional />
            <PhoneField label="Billing phone" value={f.billingPhone ?? ""} onChange={(v) => set({ billingPhone: v })} maxLength={40} optional />
          </div>
        </Section>
        <Section fieldset disabled={portalLocked} title="Customer portal address" description="Where customers open the portal. Every link FlexFreight gives a customer uses it: welcome and invite emails, password resets, shipment and invoice emails, View as customer, and this console's links into the portal.">
          <TextField
            label="Portal address"
            type="url"
            inputMode="url"
            spellCheck={false}
            disabled={portalLocked}
            value={f.portalOrigin ?? ""}
            onChange={(e) => set({ portalOrigin: e.target.value })}
            maxLength={300}
            example="https://app.flexfreight.net"
            optional
            hint={`https:// and the host only, no path. It must be on FlexFreight's own domain or an address an engineer has allowed. Leave it blank to use the server default${q.data?.portal?.fallback ? ` (${q.data.portal.fallback})` : ""}.`}
          />
          {canEditPortal ? null : <Banner tone="info">Only the console owner can change this. Owners and admins are emailed whenever it changes.</Banner>}
          <p className="ffa-small ffa-muted">Change it when the portal moves (for example to app.flexfreight.net once DNS points there); the next link uses the new address right away.</p>
          {q.data?.portal?.inUse ? <p className="ffa-small">In use now: <span className="ffx-num">{q.data.portal.inUse}</span></p> : null}
        </Section>
        <Section fieldset disabled={!canEdit} title="Customer signup">
          <Select label="Who can open an account" value={f.signupPolicy} onChange={(v) => set({ signupPolicy: v as Settings["signupPolicy"] })} options={(Object.keys(POLICY) as Settings["signupPolicy"][]).map((k) => ({ value: k, label: POLICY[k].label }))} hint={POLICY[f.signupPolicy].help} />
        </Section>
        <Section fieldset disabled={!canEdit} title="Staff alerts" description="Saved for when console email is turned on. Nothing is emailed yet.">
          <div className="ffa-switches">
            {ALERTS.map((a) => (
              <Toggle key={a.key} look="switch" label={a.label} description={a.description} checked={f[a.key]} onChange={(on) => set({ [a.key]: on })} disabled={!canEdit} />
            ))}
          </div>
        </Section>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
      {canEdit ? <WallLinks /> : null}
    </section>
  );
}

type WallLink = { id: string; name: string; createdBy: string | null; createdAt: string; lastUsedAt: string | null; revokedAt: string | null };
const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Wallboard links: a TV shows the wallboard with its own revocable key instead of someone's staff sign-in. */
function WallLinks() {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const list = useQuery({ queryKey: ["console", "wall-links"], queryFn: () => api<{ links: WallLink[] }>("/v1/console/wall-links") });
  const [name, setName] = useState("");
  const [created, setCreated] = useState<{ name: string; url: string } | null>(null);
  const create = useMutation({
    mutationFn: () => api<{ id: string; key: string }>("/v1/console/wall-links", { method: "POST", body: { name: name.trim() } }),
    onSuccess: (r) => {
      setCreated({ name: name.trim(), url: `${window.location.origin}/wall#k=${r.key}` });
      setName("");
      void qc.invalidateQueries({ queryKey: ["console", "wall-links"] });
    },
    onError: (e) => notify.error("Couldn't create the link", message(e)),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/v1/console/wall-links/${id}/revoke`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["console", "wall-links"] });
      notify.success("Link turned off", "That TV stops showing the wallboard at its next refresh.");
    },
    onError: (e) => notify.error("Couldn't turn it off", message(e)),
  });
  const active = (list.data?.links ?? []).filter((l) => !l.revokedAt);
  return (
    <Section title="Wallboard links" description="Give an office TV its own link instead of signing in as a person. A link only shows the wallboard numbers (never cost or margin) and can be turned off at any time.">
      <form
        className="ffa-inlineform"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim().length >= 2) create.mutate();
        }}
      >
        <TextField label="TV name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} example="Dispatch office TV" />
        <Button type="submit" size="lg" disabled={name.trim().length < 2} loading={create.isPending}>Create link</Button>
      </form>
      {active.length ? (
        <ul className="ffa-plainlist ffa-wallinks">
          {active.map((l) => (
            <li key={l.id} className="ffa-toggle">
              <span>
                <strong>{l.name}</strong> <span className="ffa-muted">· {l.lastUsedAt ? `last seen ${when.format(new Date(l.lastUsedAt))}` : "not opened yet"}</span>
              </span>
              <Button size="sm" variant="ghost" onClick={async () => { if (await confirm({ title: `Turn off "${l.name}"?`, body: "That TV stops showing the wallboard. You can create a new link any time.", confirmLabel: "Turn off", danger: true })) revoke.mutate(l.id); }}>Turn off</Button>
            </li>
          ))}
        </ul>
      ) : list.data ? <p className="ffa-muted ffa-small">No active wallboard links.</p> : null}
      {created ? (
        <Dialog open onClose={() => setCreated(null)} title={`Link for ${created.name}`} description="Open this on the TV. It is shown only once; if you lose it, turn it off and create a new one." footer={<Button variant="primary" onClick={() => setCreated(null)}>Done</Button>}>
          <div className="ffa-form">
            <Banner tone="warning">Keep it private: anyone with the link sees the wallboard numbers.</Banner>
            <TextField label="Wallboard link" value={created.url} readOnly onFocus={(e) => e.currentTarget.select()} />
            <Button onClick={() => void navigator.clipboard?.writeText(created.url).then(() => notify.success("Link copied"))}>Copy link</Button>
          </div>
        </Dialog>
      ) : null}
      {dialog}
    </Section>
  );
}
